
export default function GlobalExposure() {
  const exposureStrategies = [
    {
      icon: 'ri-search-line',
      title: 'Search Engine Optimization',
      description: 'Optimized for Google, Bing, Yahoo worldwide',
      features: [
        'Multi-language keyword targeting',
        'International SEO structure',
        'Schema markup implementation',
        'Local SEO for 50+ countries'
      ]
    },
    {
      icon: 'ri-share-line',
      title: 'Social Media Marketing',
      description: 'Strategic presence across global platforms',
      features: [
        'Facebook & Instagram campaigns',
        'LinkedIn business networking',
        'Twitter travel community',
        'YouTube travel content'
      ]
    },
    {
      icon: 'ri-global-line',
      title: 'International Partnerships',
      description: 'Collaborations with global travel networks',
      features: [
        'Travel blog partnerships',
        'Tourism board collaborations',
        'Affiliate marketing programs',
        'Industry association memberships'
      ]
    },
    {
      icon: 'ri-advertisement-line',
      title: 'Digital Advertising',
      description: 'Targeted ads in key markets worldwide',
      features: [
        'Google Ads in 20+ countries',
        'Facebook advertising campaigns',
        'Travel website banner ads',
        'Retargeting campaigns'
      ]
    }
  ];

  const targetMarkets = [
    { country: 'United States', flag: '🇺🇸', market: 'Primary', volume: '45%' },
    { country: 'United Kingdom', flag: '🇬🇧', market: 'Secondary', volume: '12%' },
    { country: 'Canada', flag: '🇨🇦', market: 'Secondary', volume: '8%' },
    { country: 'Australia', flag: '🇦🇺', market: 'Growth', volume: '6%' },
    { country: 'Germany', flag: '🇩🇪', market: 'Growth', volume: '5%' },
    { country: 'France', flag: '🇫🇷', market: 'Growth', volume: '4%' },
    { country: 'Japan', flag: '🇯🇵', market: 'Emerging', volume: '3%' },
    { country: 'Singapore', flag: '🇸🇬', market: 'Emerging', volume: '2%' },
  ];

  const marketingChannels = [
    {
      channel: 'Organic Search',
      percentage: 35,
      color: 'bg-green-500',
      description: 'SEO-driven traffic from search engines'
    },
    {
      channel: 'Social Media',
      percentage: 25,
      color: 'bg-blue-500',
      description: 'Facebook, Instagram, LinkedIn engagement'
    },
    {
      channel: 'Paid Advertising',
      percentage: 20,
      color: 'bg-purple-500',
      description: 'Google Ads, Facebook Ads campaigns'
    },
    {
      channel: 'Direct Traffic',
      percentage: 12,
      color: 'bg-orange-500',
      description: 'Brand recognition and repeat visitors'
    },
    {
      channel: 'Referrals',
      percentage: 8,
      color: 'bg-teal-500',
      description: 'Partner websites and affiliate links'
    }
  ];

  return (
    <section className="py-20 bg-gray-900">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Global Marketing Strategy
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto">
            Our comprehensive approach to gaining worldwide exposure and reaching travelers across the globe.
          </p>
        </div>

        {/* Marketing Strategies */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
          {exposureStrategies.map((strategy, index) => (
            <div key={index} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl mx-auto mb-6">
                <i className={`${strategy.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">{strategy.title}</h3>
              <p className="text-gray-400 mb-4">{strategy.description}</p>
              
              <ul className="space-y-2">
                {strategy.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-sm text-gray-300">
                    <div className="w-4 h-4 flex items-center justify-center mr-2">
                      <i className="ri-check-line text-green-400"></i>
                    </div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Target Markets */}
        <div className="bg-gray-800/30 rounded-2xl p-8 border border-gray-700 mb-20">
          <h3 className="text-3xl font-bold text-white text-center mb-12">Global Target Markets</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {targetMarkets.map((market, index) => (
              <div key={index} className="bg-gray-800/50 rounded-lg p-6 text-center border border-gray-600">
                <div className="text-4xl mb-3">{market.flag}</div>
                <h4 className="text-white font-semibold mb-2">{market.country}</h4>
                <div className="text-sm text-gray-400 mb-2">{market.market} Market</div>
                <div className="text-2xl font-bold text-blue-400">{market.volume}</div>
                <div className="text-xs text-gray-500">Target Volume</div>
              </div>
            ))}
          </div>
        </div>

        {/* Marketing Channels Distribution */}
        <div className="grid lg:grid-cols-2 gap-12">
          <div>
            <h3 className="text-3xl font-bold text-white mb-8">Traffic Sources Strategy</h3>
            
            <div className="space-y-6">
              {marketingChannels.map((channel, index) => (
                <div key={index} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-white font-medium">{channel.channel}</span>
                    <span className="text-blue-400 font-semibold">{channel.percentage}%</span>
                  </div>
                  
                  <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
                    <div 
                      className={`${channel.color} h-2 rounded-full transition-all duration-500`}
                      style={{ width: `${channel.percentage}%` }}
                    ></div>
                  </div>
                  
                  <p className="text-sm text-gray-400">{channel.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-3xl font-bold text-white mb-8">Exposure Tactics</h3>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg p-6 border border-blue-800/50">
                <div className="w-12 h-12 flex items-center justify-center bg-blue-600 rounded-lg mb-4">
                  <i className="ri-seo-line text-xl text-white"></i>
                </div>
                <h4 className="text-white font-semibold mb-2">Content Marketing</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• Travel guides and destination content</li>
                  <li>• Flight booking tutorials and tips</li>
                  <li>• Industry news and trend analysis</li>
                  <li>• Multi-language content creation</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-green-900/30 to-teal-900/30 rounded-lg p-6 border border-green-800/50">
                <div className="w-12 h-12 flex items-center justify-center bg-green-600 rounded-lg mb-4">
                  <i className="ri-team-line text-xl text-white"></i>
                </div>
                <h4 className="text-white font-semibold mb-2">Influencer Partnerships</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• Travel blogger collaborations</li>
                  <li>• Social media influencer campaigns</li>
                  <li>• YouTube travel channel partnerships</li>
                  <li>• Instagram travel photography features</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 rounded-lg p-6 border border-orange-800/50">
                <div className="w-12 h-12 flex items-center justify-center bg-orange-600 rounded-lg mb-4">
                  <i className="ri-megaphone-line text-xl text-white"></i>
                </div>
                <h4 className="text-white font-semibold mb-2">PR & Media Outreach</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• Travel industry press releases</li>
                  <li>• Trade publication features</li>
                  <li>• Conference and event participation</li>
                  <li>• Award submissions and recognition</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8">
            <h3 className="text-3xl font-bold text-white mb-4">Ready to Go Global?</h3>
            <p className="text-xl text-blue-100 mb-6 max-w-2xl mx-auto">
              Join thousands of travelers worldwide who trust MAVESKY for their flight booking needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
                Start Marketing Campaign
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer">
                View Analytics
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
